import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { LoadingState } from "@/components/ui/States";
import { MonoText } from "@/components/ui/CopyButton";

interface Props {
  children: ReactNode;
  requireOwner?: boolean;
}

/**
 * ProtectedRoute — guard untuk halaman yang butuh auth.
 *
 * - Jika auth masih loading → tampilkan LoadingState
 * - Jika tidak ada user → tampilkan prompt untuk sign in
 * - Jika butuh owner & user.role !== 'owner' → tampilkan forbidden
 */
export function ProtectedRoute({ children, requireOwner = false }: Props) {
  const { user, loading } = useAuth();

  if (loading) {
    return <LoadingState label="VERIFYING_SESSION" />;
  }

  if (!user) {
    return (
      <div className="max-w-xl mx-auto px-4 py-20 text-center">
        <MonoText size="sm" color="status-pending">[ AUTH_REQUIRED ]</MonoText>
        <h1 className="mt-4 font-sans text-2xl text-ink-primary">
          Authentication required
        </h1>
        <p className="mt-2 font-mono text-xs text-ink-muted">
          Sign in with Google to access this page.
        </p>
        <Link to="/" className="inline-block mt-6 font-mono text-2xs uppercase tracking-widest text-ink-muted hover:text-terminal">
          ← back to home
        </Link>
      </div>
    );
  }

  if (requireOwner && user.role !== "owner") {
    return (
      <div className="max-w-xl mx-auto px-4 py-20 text-center">
        <MonoText size="sm" color="status-failed">[ ACCESS_DENIED ]</MonoText>
        <h1 className="mt-4 font-sans text-2xl text-ink-primary">
          Owner privileges required
        </h1>
        <p className="mt-2 font-mono text-xs text-ink-muted">
          Your account does not have owner access. Contact the system administrator.
        </p>
        <Link to="/" className="inline-block mt-6 font-mono text-2xs uppercase tracking-widest text-ink-muted hover:text-terminal">
          ← back to home
        </Link>
      </div>
    );
  }

  return <>{children}</>;
}